import { isMeterAccessibility, type MeterAccessibility } from '@enmon/commons/lib/enmon/meters/accessibility';
import { reviewSectionLabel } from '@/features/inventorization/review-i18n';
import { Button, Card, Flex, Space, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import type { InventorizationTechnicalDetails } from '@/features/inventorization/domain/inventorization-technical';
import { INVENTORIZE_REVIEW_STATUSES } from '@/features/inventorization/domain/review/inventorization-review-status';
import type { InventorizeReviewField } from '@/features/inventorization/domain/review/inventorize-review-fields';
import { messages } from '@/features/inventorization/messages';
import { AccessibilityPicker } from '@/routers/inventorization/components/AccessibilityPicker';
import { ReviewStatusTag } from '@/routers/inventorization/components/ReviewStatusTag';

interface AccessibilityFieldWizardProps {
  field: InventorizeReviewField;
  onBack: () => void;
  onSaveTechnical: (patch: Partial<InventorizationTechnicalDetails>) => Promise<unknown> | unknown;
  technical: InventorizationTechnicalDetails;
}

export function AccessibilityFieldWizard({ field, onBack, onSaveTechnical, technical }: AccessibilityFieldWizardProps) {
  const intl = useIntl();
  const [selectedValues, setSelectedValues] = useState<MeterAccessibility[]>(() =>
    readAccessibilitySelection(field, technical),
  );

  useEffect(() => {
    setSelectedValues(readAccessibilitySelection(field, technical));
  }, [field, technical]);

  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Flex align="flex-start" gap="middle" justify="space-between" wrap>
          <div>
            <Typography.Text type="secondary">{reviewSectionLabel(field.section)}</Typography.Text>
            <Typography.Title level={3} style={{ marginTop: 0 }}>
              {field.label}
            </Typography.Title>
            <Typography.Paragraph type="secondary">
              {intl.formatMessage(messages.fieldWizardAccessibilityDescription)}
            </Typography.Paragraph>
          </div>
          <ReviewStatusTag status={field.status} />
        </Flex>

        <AccessibilityPicker onChange={setSelectedValues} value={selectedValues} />

        <Flex gap="small" justify="space-between" wrap>
          <Button onClick={onBack}>{intl.formatMessage(messages.reviewActionsBackToReview)}</Button>
          <Button
            onClick={() => {
              onSaveTechnical({ accessibility: selectedValues.length > 0 ? selectedValues : null });
              onBack();
            }}
            type="primary"
          >
            {intl.formatMessage(messages.reviewActionsSaveField)}
          </Button>
        </Flex>
      </Space>
    </Card>
  );
}

function readAccessibilitySelection(
  field: InventorizeReviewField,
  technical: InventorizationTechnicalDetails,
): MeterAccessibility[] {
  if (field.status === INVENTORIZE_REVIEW_STATUSES.parsed || field.status === INVENTORIZE_REVIEW_STATUSES.unsure) {
    const parsedValues = parseAccessibilityValues(field.value);

    if (parsedValues.length > 0) {
      return parsedValues;
    }
  }

  return technical.accessibility ?? [];
}

function parseAccessibilityValues(value: string) {
  return value
    .split(',')
    .map(item => item.trim())
    .filter(isMeterAccessibility);
}
